import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Search, Edit, Trash2, UserPlus, Shield, X, RefreshCw, User } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { cn } from '../../lib/utils';
import LimitWarningDialog from '../../components/LimitWarningDialog';

const PERMISSION_LABELS = {
  can_manage_users: 'Manage Users',
  can_delete_users: 'Delete Users',
  can_manage_services: 'Manage Services',
  can_manage_orders: 'Manage Orders',
  can_manage_kb: 'Manage Knowledge Base',
  can_send_notifications: 'Send Notifications',
  can_access_billing: 'Access Billing',
  can_manage_team: 'Manage Team',
  can_access_company_settings: 'Company Settings',
};

export default function AdminTeam() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'manager',
    permissions: {
      can_manage_users: true,
      can_delete_users: false,
      can_manage_services: true,
      can_manage_orders: true,
      can_manage_kb: true,
      can_send_notifications: true,
      can_access_billing: false,
      can_manage_team: false,
      can_access_company_settings: false,
    }
  });
  const [creating, setCreating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [searchQuery, filterRole]);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getTeamMembers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      full_name: user.full_name,
      email: user.email,
      company_name: user.company_name || '',
      phone: user.phone || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    try {
      await adminAPI.updateUser(selectedUser.id, editForm);
      toast.success('User updated');
      setShowEditDialog(false);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleCreateTeamMember = async () => {
    if (!createForm.email || !createForm.full_name || !createForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await adminAPI.createTeamMember(createForm);
      if (response.data?.limit_reached) {
        setLimitInfo(response.data);
        setShowLimitDialog(true);
        return;
      }
      toast.success('Team member created');
      setShowCreateDialog(false);
      setCreateForm({
        email: '',
        full_name: '',
        password: '',
        role: 'manager',
        permissions: {
          can_manage_users: true,
          can_delete_users: false,
          can_manage_services: true,
          can_manage_orders: true,
          can_manage_kb: true,
          can_send_notifications: true,
          can_access_billing: false,
          can_manage_team: false,
          can_access_company_settings: false,
        }
      });
      loadUsers();
    } catch (error) {
      if (error.response?.data?.limit_reached) {
        setLimitInfo(error.response.data);
        setShowLimitDialog(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create team member');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await adminAPI.deleteUser(userToDelete.id);
      toast.success('User deleted');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const totalTeam = users.length;
  const adminCount = users.filter(u => u.role === 'admin').length;
  const managerCount = users.filter(u => u.role === 'manager').length;

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-stone-900">Team</h1>
            <p className="text-xs text-stone-500 mt-0.5">{totalTeam} team members</p>
          </div>
          <button 
            onClick={() => setShowCreateDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-stone-900 rounded hover:bg-stone-800 transition-all"
          >
            <UserPlus className="w-3 h-3" />
            Add Member
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border border-stone-100 rounded p-3 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-stone-900">{totalTeam}</p>
                <p className="text-[10px] text-stone-500 uppercase">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-100 rounded p-3 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <Shield className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-stone-900">{adminCount}</p>
                <p className="text-[10px] text-stone-500 uppercase">Admins</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-stone-100 rounded p-3 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <User className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-stone-900">{managerCount}</p>
                <p className="text-[10px] text-stone-500 uppercase">Managers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-stone-100 rounded p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
              <input
                type="text"
                placeholder="Search team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-8 pl-8 pr-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400 transition-colors"
              />
            </div>

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="h-8 px-2 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
            </select>
          </div>
        </div>

        {/* Team List */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-5 h-5 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="bg-white border border-stone-100 rounded overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50">
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Member</th>
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Contact</th>
                  <th className="text-center p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Role</th>
                  <th className="text-right p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Joined</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-stone-50 transition-colors">
                    <td className="p-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-xs font-semibold text-stone-600">
                          {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <p className="text-xs font-medium text-stone-900">{user.full_name}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      <p className="text-xs text-stone-600">{user.email}</p>
                      {user.phone && <p className="text-[10px] text-stone-400">{user.phone}</p>}
                    </td>
                    <td className="p-3 text-center">
                      <span className={cn(
                        "inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full",
                        user.role === 'admin' ? "bg-purple-100 text-purple-700" : "bg-green-100 text-green-700"
                      )}>
                        {user.role === 'admin' ? 'Admin' : 'Manager'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <p className="text-[10px] text-stone-500">{formatDate(user.created_at)}</p>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-1.5 rounded hover:bg-stone-100 transition-colors"
                        >
                          <Edit className="w-3.5 h-3.5 text-stone-400" />
                        </button>
                        {user.role !== 'admin' && (
                          <button
                            onClick={() => { setUserToDelete(user); setShowDeleteDialog(true); }}
                            className="p-1.5 rounded hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-xs text-stone-500">No team members found</p>
              </div>
            )}
          </div>
        )}

        {/* Create Modal */}
        {showCreateDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded w-full max-w-md max-h-[90vh] overflow-auto m-4">
              <div className="flex items-center justify-between p-4 border-b border-stone-100">
                <h3 className="text-sm font-semibold text-stone-900">Add Team Member</h3>
                <button onClick={() => setShowCreateDialog(false)} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={createForm.full_name}
                    onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Email *</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Password *</label>
                  <input
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Role</label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                    className="w-full h-8 px-2 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  >
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-2">Permissions</label>
                  <div className="space-y-2 max-h-40 overflow-auto">
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={createForm.permissions[key]}
                          onChange={(e) => setCreateForm({
                            ...createForm,
                            permissions: { ...createForm.permissions, [key]: e.target.checked }
                          })}
                          className="w-3.5 h-3.5 rounded border-stone-300"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-4 border-t border-stone-100 bg-stone-50">
                <button
                  onClick={() => setShowCreateDialog(false)}
                  className="px-4 py-2 text-xs font-medium text-stone-600 hover:bg-stone-100 rounded transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTeamMember}
                  disabled={creating}
                  className="px-4 py-2 text-xs font-medium text-white bg-stone-900 rounded hover:bg-stone-800 transition-all disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditDialog && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded w-full max-w-md m-4">
              <div className="flex items-center justify-between p-4 border-b border-stone-100">
                <h3 className="text-sm font-semibold text-stone-900">Edit Member</h3>
                <button onClick={() => setShowEditDialog(false)} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Phone</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-4 border-t border-stone-100 bg-stone-50">
                <button
                  onClick={() => setShowEditDialog(false)}
                  className="px-4 py-2 text-xs font-medium text-stone-600 hover:bg-stone-100 rounded transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  className="px-4 py-2 text-xs font-medium text-white bg-stone-900 rounded hover:bg-stone-800 transition-all"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteDialog && userToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded w-full max-w-sm m-4 p-4">
              <h3 className="text-sm font-semibold text-stone-900 mb-2">Delete Member?</h3>
              <p className="text-xs text-stone-600 mb-4">
                Are you sure you want to delete {userToDelete.full_name}? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => { setShowDeleteDialog(false); setUserToDelete(null); }}
                  className="px-4 py-2 text-xs font-medium text-stone-600 hover:bg-stone-100 rounded transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deleting}
                  className="px-4 py-2 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-all disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        <LimitWarningDialog
          open={showLimitDialog}
          onOpenChange={setShowLimitDialog}
          limitInfo={limitInfo}
        />
      </div>
    </Layout>
  );
}
